requestAnimationFrame(() => {
  document.addEventListener('alpine:init', () => {
    const executedQuickViewInitScripts = new Set();
    const quickViewExternalScriptLoads = new Map();

    const quickViewRequiredAssets = [
      {
        pattern: /custom-dimensions-picker\.js(?:\?|$)/,
        isLoaded: () => window.CustomDimensionsPickerLoaded,
        source: () => window.xQuickView && window.xQuickView.customDimensionsAsset
      },
      {
        pattern: /custom-option\.js(?:\?|$)/,
        isLoaded: () => window.CustomOptionLoaded,
        source: () => window.xQuickView && window.xQuickView.customOptionAsset
      }
    ];

    const ensureQuickViewProductAssets = (container) => {
      const loads = quickViewRequiredAssets.map((asset) => {
        if (asset.isLoaded()) return Promise.resolve();

        const assetScript = Array.from(container.querySelectorAll('script[src]')).find((script) => {
          return asset.pattern.test(script.src);
        });

        const source = assetScript ? assetScript.src : asset.source();
        if (!source) return Promise.resolve();
        if (quickViewExternalScriptLoads.has(source)) {
          return quickViewExternalScriptLoads.get(source);
        }

        const loadPromise = new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = source;
          script.onload = resolve;
          script.onerror = () => {
            console.warn('Quick view product asset failed to load:', source);
            resolve();
          };
          document.head.appendChild(script);
        });

        quickViewExternalScriptLoads.set(source, loadPromise);
        return loadPromise;
      });

      return Promise.all(loads);
    };

    const namespaceQuickViewControls = (container) => {
      const idMap = new Map();
      const suffix = '-quick-view';

      container.querySelectorAll('form[id], input[id], select[id], textarea[id], button[id]').forEach((element) => {
        // A form control named "id" can shadow HTMLFormElement.id with the
        // control object. Read/write the attribute so this is always a string.
        const originalId = element.getAttribute('id') || '';
        if (!originalId || originalId.endsWith(suffix)) return;

        const quickViewId = originalId + suffix;
        idMap.set(originalId, quickViewId);
        element.setAttribute('id', quickViewId);
      });

      const replaceReference = (element, attribute, multiple) => {
        const value = element.getAttribute(attribute);
        if (!value) return;

        const resolveReference = (id) => {
          if (idMap.has(id)) return idMap.get(id);
          if (!id.endsWith(suffix) && document.getElementById(id + suffix)) return id + suffix;
          return id;
        };

        const nextValue = multiple
          ? value.split(/\s+/).map(resolveReference).join(' ')
          : resolveReference(value);

        if (nextValue !== value) element.setAttribute(attribute, nextValue);
      };

      if (idMap.size) {
        container.querySelectorAll('[for]').forEach((element) => replaceReference(element, 'for', false));
        container.querySelectorAll('[form]').forEach((element) => replaceReference(element, 'form', false));
        container.querySelectorAll('[data-resource]').forEach((element) => replaceReference(element, 'data-resource', false));
        container.querySelectorAll('[aria-controls], [aria-describedby], [aria-labelledby]').forEach((element) => {
          replaceReference(element, 'aria-controls', true);
          replaceReference(element, 'aria-describedby', true);
          replaceReference(element, 'aria-labelledby', true);
        });
      }

      container.querySelectorAll('[data-custom-dimensions-picker]').forEach((picker) => {
        // A Quick View must behave like a fresh PDP picker. Keep the compatible
        // choices available, but never silently select a later dimension.
        picker.setAttribute('data-disable-dimension-autofill', 'true');
        picker.setAttribute('data-reset-dimensions-on-inject', 'true');

        ['data-dimensions-dedupe-key', 'data-dimensions-instance'].forEach((attribute) => {
          const value = picker.getAttribute(attribute);
          if (value && !value.endsWith(suffix)) {
            picker.setAttribute(attribute, value + suffix);
          }
        });
      });
    };

    window.refreshQuickViewOptionControls = (scope) => {
      const container = scope && scope.closest && scope.closest('#quickview-product-content');
      if (!container) return;

      namespaceQuickViewControls(scope);
      container.querySelectorAll('[data-custom-dimensions-picker]').forEach((picker) => {
        syncQuickViewDimensionAvailability(picker);
        revealQuickViewLiveOptions(picker);
      });
      document.dispatchEvent(new CustomEvent('custom:color-options-updated', {
        detail: { source: 'quick-view-variant-update', root: scope }
      }));
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: { source: 'quick-view-variant-update', root: scope }
      }));

      if (window.CustomTooltipPosition && typeof window.CustomTooltipPosition.init === 'function') {
        window.CustomTooltipPosition.init(scope);
      }
    };

    const getQuickViewDimensionValue = (variant, position) => {
      return String(variant && variant['option' + position] || '').trim();
    };

    const getQuickViewSelectedDimensionValue = (column) => {
      const selected = column.querySelector('input[type="radio"]:checked:not(:disabled)');
      return selected && selected.getAttribute('data-custom-dimension-radio') !== 'true'
        ? String(selected.value || '').trim()
        : '';
    };

    const setQuickViewDimensionDisabled = (input, disabled) => {
      const label = input.nextElementSibling && input.nextElementSibling.tagName === 'LABEL'
        ? input.nextElementSibling
        : null;

      input.disabled = disabled;
      input.classList.toggle('disabled', disabled);
      input.classList.toggle('is-custom-value-option-disabled', disabled);

      if (label) {
        label.classList.toggle('is-disabled', disabled);
        label.classList.toggle('is-custom-value-option-disabled', disabled);
        label.setAttribute('aria-disabled', String(disabled));
        label.setAttribute('tabindex', disabled ? '-1' : '0');
      }
    };

    const syncQuickViewDimensionAvailability = (picker) => {
      if (!picker || !picker.closest('#QuickView')) return;
      if (picker.classList.contains('custom-dimensions-picker--custom-mode')) return;

      const columns = Array.from(picker.querySelectorAll('[data-dimension-option]'));
      const source = picker.querySelector('[data-dimension-variants]');
      if (columns.length < 2 || !source) return;

      let variants = [];
      try {
        variants = JSON.parse(source.textContent || '[]');
      } catch (error) {
        return;
      }

      columns.forEach((column, columnIndex) => {
        const previousColumnsReady = columnIndex === 0 || columns
          .slice(0, columnIndex)
          .every((previousColumn) => !!getQuickViewSelectedDimensionValue(previousColumn));
        const position = Number(column.getAttribute('data-option-index') || 0) + 1;

        column.querySelectorAll('input[type="radio"]').forEach((input) => {
          if (input.getAttribute('data-custom-dimension-radio') === 'true') return;

          const value = String(input.value || '').trim();
          const disabled = columnIndex > 0 && (
            !previousColumnsReady || !variants.some((variant) => {
              if (!variant || variant.available === false || getQuickViewDimensionValue(variant, position) !== value) {
                return false;
              }

              return columns.slice(0, columnIndex).every((previousColumn) => {
                const previousValue = getQuickViewSelectedDimensionValue(previousColumn);
                const previousPosition = Number(previousColumn.getAttribute('data-option-index') || 0) + 1;
                return previousValue && getQuickViewDimensionValue(variant, previousPosition) === previousValue;
              });
            })
          );

          if (disabled && input.checked) {
            input.checked = false;
            input.removeAttribute('checked');
          }

          setQuickViewDimensionDisabled(input, disabled);
        });
      });
    };

    const revealQuickViewLiveOptions = (picker) => {
      if (!picker || !picker.closest('#QuickView')) return;

      const columns = Array.from(picker.querySelectorAll('[data-dimension-option]'));
      const selectionComplete = columns.length > 0 && columns.every((column) => {
        return !!getQuickViewSelectedDimensionValue(column);
      });
      if (!selectionComplete) return;

      const container = picker.closest('#quickview-product-content');
      if (!container) return;

      container.querySelectorAll(
        '[data-initial-aggregate-color-preview], ' +
        '[data-initial-aggregate-normal-options], ' +
        '[data-initial-aggregate-custom-modifications]'
      ).forEach((preview) => preview.remove());

      container.querySelectorAll(
        '[data-live-variant-color-options], ' +
        '[data-live-normal-metafield-options], ' +
        '[data-live-custom-modification-options]'
      ).forEach((liveBlock) => {
        liveBlock.hidden = false;
        liveBlock.removeAttribute('hidden');
        liveBlock.style.removeProperty('display');
        liveBlock.setAttribute('aria-hidden', 'false');
      });

      document.dispatchEvent(new CustomEvent('custom:color-options-updated', {
        detail: { source: 'quick-view-dimensions-complete', root: container }
      }));
    };

    const clearQuickViewDimensionColumnsAfter = (picker, selectedColumnIndex) => {
      const columns = Array.from(picker.querySelectorAll('[data-dimension-option]'));

      columns.slice(selectedColumnIndex + 1).forEach((column) => {
        column.querySelectorAll('input[type="radio"]').forEach((radio) => {
          radio.checked = false;
          radio.removeAttribute('checked');
        });
      });
    };

    const resetQuickViewDimensionSelection = (picker) => {
      if (!picker || picker.getAttribute('data-reset-dimensions-on-inject') !== 'true') return;

      picker.querySelectorAll('[data-dimension-option] input[type="radio"]').forEach((radio) => {
        if (radio.getAttribute('data-custom-dimension-radio') === 'true') return;
        radio.checked = false;
        radio.removeAttribute('checked');
      });

      picker.removeAttribute('data-synced-variant-id');
      syncQuickViewDimensionAvailability(picker);
    };

    const handleQuickViewDimensionChange = (event) => {
      const input = event.target.closest('[data-custom-dimensions-picker] .custom-dimensions-picker__input');
      if (!input || input.getAttribute('data-custom-dimension-radio') === 'true') return;

      const picker = input.closest('[data-custom-dimensions-picker]');
      if (!picker || !picker.closest('#QuickView')) return;

      const columns = Array.from(picker.querySelectorAll('[data-dimension-option]'));
      const selectedColumnIndex = columns.indexOf(input.closest('[data-dimension-option]'));
      if (selectedColumnIndex < 0) return;

      clearQuickViewDimensionColumnsAfter(picker, selectedColumnIndex);
      syncQuickViewDimensionAvailability(picker);
      revealQuickViewLiveOptions(picker);

      // The injected PDP script can restore a server variant after this
      // listener runs. Reassert the Quick View state after that work settles.
      setTimeout(() => {
        clearQuickViewDimensionColumnsAfter(picker, selectedColumnIndex);
        syncQuickViewDimensionAvailability(picker);
        revealQuickViewLiveOptions(picker);
      }, 0);
    };

    if (!window.QuickViewDimensionAvailabilityBound) {
      window.QuickViewDimensionAvailabilityBound = true;
      document.addEventListener('change', handleQuickViewDimensionChange, true);
    }

    const getQuickViewOptionInput = (label) => {
      const quickView = label.closest('#QuickView');
      if (!quickView) return null;

      const item = label.closest(
        '.block-color-texture-card__color-item, .block-color-texture-card__texture-item, ' +
        '.block-color-texture-card__decide, .block-addon-option__item, [data-option-group="quote-only"]'
      );
      const adjacentInput = label.previousElementSibling;

      if (adjacentInput && adjacentInput.matches('input[type="radio"], input[type="checkbox"]')) {
        return adjacentInput;
      }

      if (item) {
        return item.querySelector('input[type="radio"], input[type="checkbox"]');
      }

      return null;
    };

    const normalizeQuickViewOptionValue = (value) => {
      return String(value || '').replace(/\s+/g, ' ').trim();
    };

    const syncQuickViewTextureOptions = (input) => {
      if (!input || !input.matches('.color-option[data-custom-color-media]') || !input.checked) return;

      const card = input.closest('[data-color-texture-card]');
      if (!card || !card.closest('#QuickView')) return;

      const selectedColor = normalizeQuickViewOptionValue(input.value);
      let hasVisibleTexture = false;

      card.querySelectorAll('.block-color-texture-card__texture-group-heading').forEach((heading) => {
        const matches = normalizeQuickViewOptionValue(heading.getAttribute('data-color')) === selectedColor;
        heading.style.display = matches ? '' : 'none';
        heading.classList.toggle('is-visible', matches);
      });

      card.querySelectorAll('.block-color-texture-card__texture-item, .color-dependent-option').forEach((option) => {
        const matches = normalizeQuickViewOptionValue(option.getAttribute('data-color')) === selectedColor;
        const optionInput = option.querySelector('input');

        option.hidden = false;
        option.style.display = matches ? '' : 'none';
        option.classList.toggle('is-visible', matches);

        if (!matches && optionInput) {
          optionInput.checked = false;
          optionInput.removeAttribute('checked');
        }

        if (matches) hasVisibleTexture = true;
      });

      const textureGrid = card.querySelector('[data-texture-grid]');
      if (textureGrid) {
        textureGrid.hidden = !hasVisibleTexture;
        textureGrid.style.display = hasVisibleTexture ? '' : 'none';
      }

      const textureTitle = card.querySelector('[data-texture-title]');
      if (textureTitle) textureTitle.hidden = true;

      const decideLater = card.querySelector('[data-decide-later-input="true"]');
      if (decideLater) {
        decideLater.checked = false;
        decideLater.removeAttribute('checked');
      }
    };

    const handleQuickViewOptionClick = (event) => {
      if (event.target.closest('.block-addon-option__info-wrap, .tooltip-content')) return;

      const label = event.target.closest(
        '#QuickView .block-color-texture-card label[for], ' +
        '#QuickView .block-addon-option__label[for], ' +
        '#QuickView [data-option-group="quote-only"] label[for]'
      );
      if (!label) return;

      const input = getQuickViewOptionInput(label);
      if (!input || input.disabled) return;

      const isToggleableAddon = input.matches(
        '.block-addon-option__input, [data-option-group="quote-only"] input[type="radio"]'
      );
      const expectedChecked = isToggleableAddon ? !input.checked : true;

      setTimeout(() => {
        if (!input.isConnected) return;

        if (input.checked !== expectedChecked) {
          input.checked = expectedChecked;
          if (expectedChecked) {
            input.setAttribute('checked', 'checked');
          } else {
            input.removeAttribute('checked');
          }
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        syncQuickViewTextureOptions(input);
      }, 0);
    };

    if (!window.QuickViewOptionClickFallbackBound) {
      window.QuickViewOptionClickFallbackBound = true;
      document.addEventListener('click', handleQuickViewOptionClick, true);
    }

    if (!window.QuickViewTextureSyncBound) {
      window.QuickViewTextureSyncBound = true;
      document.addEventListener('change', (event) => {
        const input = event.target.closest('#QuickView .color-option[data-custom-color-media]');
        if (input) syncQuickViewTextureOptions(input);
      }, true);
    }

    const refreshQuickViewContent = (container) => {
      if (!container) return;

      namespaceQuickViewControls(container);

      const activeForm = container.querySelector('form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
      if (activeForm) {
        window.CustomQuoteActiveForm = activeForm;
      }

      container.querySelectorAll('script').forEach((script) => {
        const type = (script.getAttribute('type') || '').trim().toLowerCase();
        if (script.src || (type && type !== 'text/javascript' && type !== 'application/javascript')) return;

        const code = script.textContent || '';
        const runOnceKey = code.indexOf('window.CustomColorTextureControls') !== -1 ||
          code.indexOf('handleAggregatePreviewInteraction') !== -1
          ? 'custom-options-inline-init'
          : '';

        if (runOnceKey && executedQuickViewInitScripts.has(runOnceKey)) return;

        try {
          (new Function(code))();
          if (runOnceKey) executedQuickViewInitScripts.add(runOnceKey);
        } catch (error) {
          console.warn('Quick view script init failed:', error);
        }
      });

      ensureQuickViewProductAssets(container).then(() => {
        // Quick View content is injected after Alpine's initial page scan.
        // Initialize only the media gallery to avoid reinitializing custom option controls.
        if (window.Alpine && typeof Alpine.initTree === 'function') {
          const mediaRoots = Array.from(container.querySelectorAll('[id^="product-media-"]'));
          const rootsToInitialize = mediaRoots.length
            ? mediaRoots
            : Array.from(container.querySelectorAll('[id^="media-gallery-"]'));

          rootsToInitialize.forEach((root) => Alpine.initTree(root));
        }

        document.dispatchEvent(new CustomEvent('custom-dimensions:reset-injected', { detail: { root: container } }));
        container.querySelectorAll('[data-custom-dimensions-picker]').forEach(syncQuickViewDimensionAvailability);
        container.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('custom:color-options-updated', { detail: { source: 'quick-view' } }));
        document.dispatchEvent(new CustomEvent('variant:change', { detail: { source: 'quick-view' } }));
        document.dispatchEvent(new CustomEvent('product:variant-change', { detail: { source: 'quick-view' } }));
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('custom-dimensions:reset-injected', { detail: { root: container } }));
          container.querySelectorAll('[data-custom-dimensions-picker]').forEach(resetQuickViewDimensionSelection);
        }, 0);

        if (typeof window.updateAllQuoteHiddenProperties === 'function') {
          window.updateAllQuoteHiddenProperties();
        }

        if (typeof window.syncCurrentLineItemPropertiesToForm === 'function' && activeForm) {
          window.syncCurrentLineItemPropertiesToForm(activeForm);
        }

        if (window.CustomTooltipPosition && typeof window.CustomTooltipPosition.init === 'function') {
          window.CustomTooltipPosition.init(container);
        }
      });
    };

    Alpine.store('xQuickView', {
      sectionId: window.xQuickView.sectionId,
      enabled: window.xQuickView.enabled,
      buttonLabel: window.xQuickView.buttonLabel,
      show_atc_button: window.xQuickView.show_atc_button,
      btn_atc_bottom: window.xQuickView.atc_btn_bottom,
      atc_btn_image_desktop: window.xQuickView.atc_btn_image_desktop,
      show: false,
      loading: false,
      currentVariant: '',
      cachedResults: [],
      cachedfetch: [],
      loadingUrls: [],
      loadedChooseOptions: [],
      loadedChooseOptionsID: [],
      selected: false,
      loadingChooseOption: false,
      isMiniCart: false,
      buttonQuickView: "",
      addListener() {
        document.addEventListener('eurus:cart:items-changed', () => {
          this.cachedResults = [];
        });
      },
      showBtn(enable, showATCButton, textBtn) {
        this.enabled = enable;
        this.buttonLabel = textBtn;
        this.show_atc_button = showATCButton;
      },
      load(url, el, optionId) {
        this.loading = true;

        requestAnimationFrame(() => {
          setTimeout(() => { 
            if (this.buttonQuickView == '') {
              this.buttonQuickView = el;
            }
            let variant = document.getElementById('current-variant-' + optionId)?.innerText;
            let productUrl = variant?`${url}?variant=${variant}&section_id=${this.sectionId}`:`${url}?section_id=${this.sectionId}`;
            productUrl = productUrl.replace(/\s+/g, '');
            
            if (this.cachedResults[productUrl]) {
              const container = document.getElementById('quickview-product-content');
              container.innerHTML = this.cachedResults[productUrl];
              refreshQuickViewContent(container);
              this.loading = false;
              return true;
            }
            
            if (this.cachedfetch[productUrl]) {
              return true;
            }

            this.cachedfetch[productUrl] = true;
            fetch(productUrl)
              .then(reponse => {
                return reponse.text();
              })
              .then((response) => {
                const parser = new DOMParser();
                const content = parser.parseFromString(response,'text/html').getElementById("quickview-product-content").innerHTML;
                const container = document.getElementById('quickview-product-content');
                container.innerHTML = content;
                refreshQuickViewContent(container);
                this.cachedResults[productUrl] = content;
              })
              .finally(() => {
                this.loading = false;
                this.cachedfetch[productUrl] = false;
              })

            return true;
          }, 0)
        });
      },
      async loadChooseOptions(url, el, optionId, index) {
        setTimeout(async () => { 
          let getVariant = document.getElementById('current-variant-' + optionId)?.innerText;
          let urlProduct = `${url}?section_id=choose-option&page=${index}`;
          
          let destinationElm = document.getElementById('choose-options-' + optionId)?.querySelector('.choose-options');
          let destinationElmMobile = document.getElementById('choose-options-mobile');
          let loadingEl = document.getElementById('choose-options-' + optionId)?.querySelector('.icon-loading');
          
          if (this.loadedChooseOptions[urlProduct]) {
            if (window.innerWidth > 767) {
              if (!el.closest('.card-product').querySelector('#choose-options-' + optionId)?.querySelector('.choose-options-content')) {
                destinationElm && (destinationElm.innerHTML = this.loadedChooseOptions[urlProduct]);
                destinationElmMobile && (destinationElmMobile.innerHTML = '');  
              }
              if (destinationElm) {
                if (destinationElm.querySelector(".add_to_cart_button")) {
                  destinationElm.querySelector(".add_to_cart_button").style.display="block";
                }
                if (destinationElm.querySelector(".label-btn-quickview")) {
                  destinationElm.querySelector(".label-btn-quickview").style.display="none";
                }
              }
            } else {
              if (!el.closest('.card-product').querySelector('#choose-options-mobile')) {
                destinationElmMobile && (destinationElmMobile.innerHTML = this.loadedChooseOptions[urlProduct]);
                destinationElm && (destinationElm.innerHTML = '');
              }
              if (destinationElmMobile) {
                if (destinationElmMobile.querySelector(".add_to_cart_button")) {
                  destinationElmMobile.querySelector(".add_to_cart_button").style.display="block";
                }
                if (destinationElmMobile.querySelector(".label-btn-quickview")) {
                  destinationElmMobile.querySelector(".label-btn-quickview").style.display="none";
                }
              }
            }
            return true;
          }
          
          try {
            if (loadingEl) {
              loadingEl.classList.remove('hidden');
            }
            this.loadingChooseOption = true;
            if (!this.loadingUrls.includes(urlProduct)) {
              this.loadingUrls.push(urlProduct);
              await fetch(urlProduct).then((response) => response.text()).then((content) => {
                const parser = new DOMParser();
                const parsedContent = parser.parseFromString(content, 'text/html').getElementById("choose-options-content").innerHTML;
                
                if (parsedContent) {
                  if (window.innerWidth > 767) {
                    destinationElmMobile && (destinationElmMobile.innerHTML = '');
                    destinationElm && (destinationElm.innerHTML = parsedContent);
                  } else {
                    destinationElmMobile && (destinationElmMobile.innerHTML = parsedContent);
                    destinationElm && (destinationElm.innerHTML = '');
                  }
                  this.loadedChooseOptions[urlProduct] = parsedContent;
                }
                if (loadingEl) {
                  loadingEl.classList.add('hidden');
                }  
              }).finally(() => {
                const index = this.loadingUrls.indexOf(urlProduct);
                if (index !== -1) this.loadingUrls.splice(index, 1);  
                if (window.innerWidth > 767) {
                  if (destinationElm) {
                    if (destinationElm.querySelector(".add_to_cart_button")) {
                      destinationElm.querySelector(".add_to_cart_button").style.display="block";
                    }
                    if (destinationElm.querySelector(".label-btn-quickview")) {
                      destinationElm.querySelector(".label-btn-quickview").style.display="none";
                    }
                  }
                } else {
                  if (destinationElmMobile) {
                    if (destinationElmMobile.querySelector(".add_to_cart_button")) {
                      destinationElmMobile.querySelector(".add_to_cart_button").style.display="block";
                    }
                    if (destinationElmMobile.querySelector(".label-btn-quickview")) {
                      destinationElmMobile.querySelector(".label-btn-quickview").style.display="none";
                    }
                  }
                }      
              });
            }
            this.loadingChooseOption = false;
          } catch (error) {
            console.log(error);
          }
      }, 0)
      },
      open(isMiniCart = false) {
        const miniCart = Alpine.store('xMiniCart');
        if (miniCart?.open) {
          miniCart.open = false;
        }
        this.show = true;
        this.isMiniCart = isMiniCart;
        Alpine.store('xPopup').open = true;
      },
      close() {
        this.show = false;
        this.buttonQuickView = '';
        if (window.CustomQuoteActiveForm && window.CustomQuoteActiveForm.closest('#QuickView')) {
          window.CustomQuoteActiveForm = null;
        }
        if(!Alpine.store('xMiniCart').open) {
          Alpine.store('xPopup').close();
        }
      },
      focusQuickView(quickView, btnClose) {
        if ( !this.selected ) { 
          Alpine.store('xFocusElement').trapFocus(quickView, btnClose);
        }
      },
      removeFocusQuickView() {
        if ( !this.selected ) { 
          const elementActive = document.getElementById("button_quickview");
          Alpine.store('xFocusElement').removeTrapFocus(elementActive);
        }
      },
      closePopupMobile() {
        this.openPopupMobile = false;
      },
      showChooseOption(id, el) {
        this.openPopupMobile = true;
        const product = el.closest(".card-product");
        const addToCartBtn = product.querySelector('.choose-options')?.querySelector('.add_to_cart_button');
        const iconAddToCartBtn = product.querySelector('.choose-options')?.querySelector('.label-btn-quickview')
        const addToCartBtnMobile = document.querySelector('.choose-options-mobile')?.querySelector('.add_to_cart_button');
        const iconAddToCartBtnMobile = document.querySelector('.choose-options-mobile')?.querySelector('.label-btn-quickview')

        if(addToCartBtn && iconAddToCartBtn && window.innerWidth > 767){
          addToCartBtn.style.display="block"
          iconAddToCartBtn.style.display="none"
        }else if(addToCartBtnMobile && iconAddToCartBtnMobile){
          addToCartBtnMobile.style.display="block"     
          iconAddToCartBtnMobile.style.display="none"
        }       
      },
      clickInsideQuickView(evt, $el) {
        if(this.isMiniCart) {
          evt.stopPropagation();
          this.show = false;
          if (window.CustomQuoteActiveForm && window.CustomQuoteActiveForm.closest('#QuickView')) {
            window.CustomQuoteActiveForm = null;
          }
          Alpine.store('xPopup').close();
        }
      },
      clickInsideContentQuickView(evt) {
        evt.stopPropagation();
      }
    });
  });
});
